import * as React from 'react';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@radix-ui/react-collapsible';
import type { TranslationObject } from '@web-config-app/core';
import {
  BaseInput,
  Box,
  Button,
  Icon,
  InputStatusMessage,
  keyframes,
  StackLayout,
  UtilityText,
} from '@leagueplatform/genesis-core';
import { useIntl } from '@leagueplatform/locales';
import { AiAssistIconSvg } from '../../icons/ai-assist-icon.component';

const borderRotate = keyframes({
  '0%': { transform: 'translate(-50%, -50%) rotate(0deg)' },
  '100%': { transform: 'translate(-50%, -50%) rotate(360deg)' },
});

export interface AiAssistToggleButtonProps
  extends Omit<
    React.ComponentPropsWithoutRef<typeof Button>,
    'children' | 'icon'
  > {
  isOpen: boolean;
}

export const AiAssistToggleButton = React.forwardRef<
  HTMLButtonElement,
  AiAssistToggleButtonProps
>(({ isOpen, ...buttonProps }, ref) => {
  const { formatMessage } = useIntl();

  return (
    <Button
      ref={ref}
      type="button"
      quiet
      size="small"
      priority="tertiary"
      icon={isOpen ? 'interfaceChevronUp' : 'interfaceChevronDown'}
      css={{
        whiteSpace: 'nowrap',
        '& > span': {
          display: 'flex',
          alignItems: 'center',
          gap: '$quarter',
        },
      }}
      {...buttonProps}
    >
      <Icon size="$one" icon={<AiAssistIconSvg />} />
      {formatMessage({ id: 'AI_ASSIST' })}
    </Button>
  );
});
AiAssistToggleButton.displayName = 'AiAssistToggleButton';

export interface AiAssistPromptPanelProps {
  /** Id for the panel root (used by aria-controls on the toggle). */
  id?: string;
  onGenerate: (prompt: string) => Promise<void>;
  assumptionsText?: string;
  promptPlaceholder?: TranslationObject;
  /** Controlled prompt value for the textarea. When provided, onPromptChange must also be provided. */
  prompt?: string;
  /** Called when the prompt text changes (user typing or clearing). Required when prompt is provided. */
  onPromptChange?: (prompt: string) => void;
  generateLabelKey?: string;
  generatingLabelKey?: string;
  errorMessageKey?: string;
  clearPromptLabelKey?: string;
  promptInputLabelKey?: string;
  assumptionsLabelKey?: string;
}

export const AiAssistPromptPanel = ({
  id,
  onGenerate,
  assumptionsText,
  promptPlaceholder: promptPlaceholderConfig,
  prompt: controlledPrompt,
  onPromptChange,
  generateLabelKey = 'AI_ASSIST_GENERATE',
  generatingLabelKey = 'AI_ASSIST_GENERATING',
  errorMessageKey = 'AI_GENERATION_ERROR',
  clearPromptLabelKey = 'CLEAR_PROMPT',
  promptInputLabelKey = 'AI_ASSIST_PROMPT_INPUT_LABEL',
  assumptionsLabelKey = 'AI_ASSUMPTIONS_LABEL',
}: AiAssistPromptPanelProps) => {
  const { formatMessage } = useIntl();
  const assumptionsId = React.useId();
  const [internalPrompt, setInternalPrompt] = React.useState('');
  const [isGenerating, setIsGenerating] = React.useState(false);
  const [generateError, setGenerateError] = React.useState<string>();

  const isControlled = controlledPrompt !== undefined;

  if (
    process.env.NODE_ENV !== 'production' &&
    isControlled &&
    !onPromptChange
  ) {
    console.warn(
      'AiAssistPromptPanel: `prompt` was provided without `onPromptChange`. The textarea will be read-only.',
    );
  }

  const prompt = isControlled ? controlledPrompt : internalPrompt;
  const setPrompt = React.useMemo(
    () =>
      isControlled
        ? (value: string) => onPromptChange?.(value)
        : setInternalPrompt,
    [isControlled, onPromptChange],
  );

  const handleClear = React.useCallback(() => {
    setPrompt('');
    setGenerateError(undefined);
  }, [setPrompt]);

  const promptPlaceholder = React.useMemo(() => {
    if (!promptPlaceholderConfig) return undefined;

    if (promptPlaceholderConfig.text) {
      return promptPlaceholderConfig.text;
    }

    if (promptPlaceholderConfig.key) {
      return formatMessage(
        { id: promptPlaceholderConfig.key },
        promptPlaceholderConfig.values,
      );
    }

    return undefined;
  }, [formatMessage, promptPlaceholderConfig]);

  const handleGenerate = React.useCallback(async () => {
    if (!prompt.trim() || isGenerating) return;

    setGenerateError(undefined);
    setIsGenerating(true);

    try {
      await onGenerate(prompt.trim());
    } catch (error) {
      console.error('AI Assist generation failed:', error);
      const agentMessage =
        error instanceof Error &&
        'agentResponseMessage' in error &&
        typeof (error as any).agentResponseMessage === 'string' &&
        (error as any).agentResponseMessage.trim()
          ? (error as any).agentResponseMessage
          : undefined;
      setGenerateError(
        agentMessage
          ? `${formatMessage({
              id: 'AI_AGENT_RESPONSE_LABEL',
            })} ${agentMessage}`
          : formatMessage({ id: errorMessageKey }),
      );
    } finally {
      setIsGenerating(false);
    }
  }, [errorMessageKey, formatMessage, isGenerating, onGenerate, prompt]);

  return (
    <StackLayout
      id={id}
      role="region"
      aria-label={formatMessage({ id: 'AI_ASSIST' })}
      spacing="$half"
      css={{
        paddingInlineStart: '$half',
        paddingBlockEnd: '$half',
        paddingInlineEnd: '$half',
        width: '100%',
      }}
    >
      <div style={{ position: 'relative', width: '100%' }}>
        <BaseInput
          as="textarea"
          rows={3}
          value={prompt}
          onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) =>
            setPrompt(event.currentTarget.value)
          }
          aria-label={formatMessage({ id: promptInputLabelKey })}
          aria-describedby={assumptionsText ? assumptionsId : undefined}
          autoFocus
          placeholder={
            promptPlaceholder ||
            formatMessage({ id: 'AI_PROMPT_INPUT_PLACEHOLDER' })
          }
          css={{
            '& textarea': {
              paddingInlineEnd: '$twoAndHalf',
            },
          }}
        />
        {prompt.length > 0 && (
          <Button
            type="button"
            size="toolbar"
            icon="tinyClear"
            hideLabel
            aria-label={formatMessage({ id: clearPromptLabelKey })}
            onClick={handleClear}
            priority="secondary"
            css={{
              position: 'absolute',
              insetInlineEnd: '$quarter',
              insetBlockStart: '$quarter',
              border: '$none',
            }}
          >
            {formatMessage({ id: clearPromptLabelKey })}
          </Button>
        )}
      </div>
      {assumptionsText && (
        <UtilityText id={assumptionsId} size="xs">
          {`${formatMessage({
            id: assumptionsLabelKey,
          })} ${assumptionsText}`}
        </UtilityText>
      )}
      {generateError && (
        <Box role="alert" aria-live="polite">
          <InputStatusMessage inputStatus="error" hideIcon>
            {generateError}
          </InputStatusMessage>
        </Box>
      )}
      <StackLayout orientation="horizontal" spacing="$half">
        <Box
          css={{
            position: 'relative',
            display: 'inline-flex',
          }}
        >
          {isGenerating && (
            <Box
              aria-hidden="true"
              css={{
                position: 'absolute',
                inset: '-$quarter',
                borderRadius: 'calc($radii$button + var(--GDS-space-quarter))',
                overflow: 'hidden',
                pointerEvents: 'none',
                '&::before': {
                  content: '',
                  position: 'absolute',
                  insetBlockStart: '50%',
                  insetInlineStart: '50%',
                  width: '200%',
                  aspectRatio: '1',
                  background: `conic-gradient(
                    from 0deg,
                    $colors$decorativeBrandPrimaryDefault,
                    $colors$decorativeBrandPrimaryBright,
                    $colors$decorativeBrandSecondaryDefault,
                    $colors$decorativeBrandPrimaryDefault
                  )`,
                  animation: `${borderRotate} 2s linear infinite`,
                  '@media (prefers-reduced-motion: reduce)': {
                    animation: 'none',
                  },
                },
              }}
            />
          )}
          <Button
            type="button"
            size="small"
            disabled={!prompt.trim() || isGenerating}
            onClick={handleGenerate}
            css={isGenerating ? { position: 'relative' } : undefined}
          >
            {isGenerating
              ? formatMessage({ id: generatingLabelKey })
              : formatMessage({ id: generateLabelKey })}
          </Button>
        </Box>
      </StackLayout>
    </StackLayout>
  );
};

export interface AiAssistCollapsibleProps
  extends Omit<AiAssistPromptPanelProps, 'id'> {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  trailingContent?: React.ReactNode;
  toggleButtonRef?: React.Ref<HTMLButtonElement>;
}

export const AiAssistCollapsible = ({
  isOpen,
  onOpenChange,
  trailingContent,
  toggleButtonRef,
  ...panelProps
}: AiAssistCollapsibleProps) => {
  const aiAssistPanelId = React.useId();

  return (
    <Collapsible asChild open={isOpen} onOpenChange={onOpenChange}>
      <StackLayout css={{ width: '100%' }} spacing="$quarter">
        <StackLayout
          orientation="horizontal"
          verticalAlignment="center"
          css={{ width: '100%', justifyContent: 'space-between' }}
        >
          <StackLayout
            orientation="horizontal"
            verticalAlignment="center"
            css={{ flex: '0 1 auto', minWidth: 0 }}
          >
            <CollapsibleTrigger asChild>
              <AiAssistToggleButton ref={toggleButtonRef} isOpen={isOpen} />
            </CollapsibleTrigger>
          </StackLayout>
          {trailingContent}
        </StackLayout>
        <CollapsibleContent asChild>
          <Box css={{ width: '100%' }}>
            <AiAssistPromptPanel id={aiAssistPanelId} {...panelProps} />
          </Box>
        </CollapsibleContent>
      </StackLayout>
    </Collapsible>
  );
};
